/* eslint-disable no-console */
/**
 * getSaldoEdificio — ÚNICA fuente de verdad del saldo de comisión de un edificio.
 *
 * Motor extraído VERBATIM del histórico de CalculadorComisionEdificio (EECC):
 *   - Pieza A (generada/recaudada): líneas ~742-861 del EECC.
 *   - Pieza B (pagada): query de `pagosEdificio` (EECC ~662-707) + reduce (~1229).
 *   - saldo = recaudada − pagada (EECC línea ~1277).
 *
 * Rango fijo [2023-01-01 … hoy]. Recaudada = comisión de facturas con `facturafechacobro`.
 * Pagada = Σ movimientos_caja (entidad_tipo='edificio', categoria_pago='comision_edificio',
 * estado != 'anulado'), histórico completo sin filtro de fecha.
 *
 * NO crea cálculo nuevo: reutiliza calcularImporteNetoIntermediario, calcularPantallasAjustadas,
 * calcularPorcentajeComisionEdificio, explodarContratosComoFacturas, getRateForOpp — los mismos
 * que el EECC. Comparación por id numérico (regla 10.7).
 *
 * NOTA (quirk preservado del EECC): el BTL se fetchea una vez por año de ANOS_BTL y se aplana sin
 * deduplicar (getOportunidadesBTL ignora `ano` → devuelve el set completo N veces). Se replica tal
 * cual para que los números den IDÉNTICOS al EECC. Para Chile hay 1 sola venta BTL, sin impacto en
 * los edificios con comisión por PAUTA/contratos. Candidato a fix aparte.
 */
import { calcularImporteNetoIntermediario } from "./calcularComisionIntermediario";
import { getRateForOpp } from "./calculateVentas";
import { calcularPantallasAjustadas, calcularPorcentajeComisionEdificio } from "./comisionEdificiosUtil";
import { getDolarFallback, getDolarValue, loadExchangeRates } from "./exchangeRateService";
import { explodarContratosComoFacturas } from "./explodarContratosComoFacturas";
import { getClientes } from "./getClientes";
import { getContratos } from "./getContratos";
import { getEdificiosOperativos } from "./getEdificiosOperativos";
import { getFacturasComisionTodos } from "./getFacturasComisionTodos";
import { getIntermediarios } from "./getIntermediarios";
import { getOportunidadesBTL } from "./getOportunidadesBTL";
import { client } from "./supabase";

const FECHA_INICIO_HISTORICO = "2023-01-01";
// Espejo literal del EECC: 4 fetches → BTL se cuenta ×4 (ver NOTA en el header).
const ANOS_BTL = [2023, 2024, 2025, 2026];

// Membresía de edificio en CSV `edificiosparticipantes` — misma regla que el EECC (split sin trim;
// la data es CSV sin espacios, confirmado). Comparación por id (regla 10.7).
const perteneceA = (csv, edificioId) => (csv ? csv.split(",").includes(String(edificioId)) : false);

// Carga el contexto compartido UNA vez (clientes/intermediarios/contratos/BTL/tasas/pagos) para los
// edificioIds dados. El edificiosMap se arma de TODOS los operativos (lo necesita explodar contratos
// multi-edificio), igual que el EECC.
const cargarContexto = async (pais, edificioIds, operativosPreload = null) => {
  await loadExchangeRates();

  const operativos = operativosPreload || (await getEdificiosOperativos({ pais }).catch(() => []));
  const edificiosMap = Object.fromEntries((operativos || []).map((e) => [Number(e.id), e]));

  // Caso single con id no-operativo: traerlo para que computeEdificio tenga su objeto comisión.
  const faltantes = edificioIds.map(Number).filter((id) => !edificiosMap[id]);
  if (faltantes.length > 0) {
    const { data: extra } = await client.from("edificios").select("*").in("id", faltantes);
    (extra || []).forEach((e) => {
      edificiosMap[Number(e.id)] = e;
    });
  }

  const fechaFin = new Date().toISOString().split("T")[0];

  const [clientesRaw, intermediariosRaw, contratosData, pautaTodos, ...btlResults] = await Promise.all([
    getClientes({ pais }).catch(() => []),
    getIntermediarios().catch(() => []),
    getContratos({ pais }).catch(() => []),
    getFacturasComisionTodos({ edificios: edificioIds, fechas: [FECHA_INICIO_HISTORICO, fechaFin], pais }).catch(
      () => []
    ),
    ...ANOS_BTL.map(() => getOportunidadesBTL({ pais }).catch(() => [])),
  ]);

  // Normalización igual que el EECC (id Number, agencia trim)
  const clientes = (clientesRaw || []).map((c) => ({
    ...c,
    id: Number(c.id),
    agencia: c.agencia ? String(c.agencia).trim() : null,
  }));
  const intermediarios = (intermediariosRaw || []).map((i) => ({ ...i, id: Number(i.id) }));

  const facturasContratos = explodarContratosComoFacturas(
    contratosData || [],
    edificiosMap,
    FECHA_INICIO_HISTORICO,
    fechaFin
  );
  const btlData = btlResults.flat();

  // Pagada histórica (sin filtro de fecha) — misma query que el EECC.
  const { data: pagos } = await client
    .from("movimientos_caja")
    .select("entidad_id, monto")
    .eq("pais", pais)
    .eq("entidad_tipo", "edificio")
    .eq("categoria_pago", "comision_edificio")
    .neq("estado", "anulado")
    .in("entidad_id", edificioIds.map(Number));

  const pagadaPorId = {};
  (pagos || []).forEach((p) => {
    const id = Number(p.entidad_id);
    pagadaPorId[id] = (pagadaPorId[id] || 0) + Number(p.monto || 0);
  });

  return {
    edificiosMap,
    clientes,
    intermediarios,
    pautaTodos: pautaTodos || [],
    facturasContratos,
    btlData,
    pagadaPorId,
  };
};

// Calcula {generada, recaudada, ...} de UN edificio sobre el contexto. Mirror exacto del loop del EECC.
const computeEdificio = (edificioId, ctx, pais) => {
  const id = Number(edificioId);
  const moneda = pais === "Chile" ? "CLP" : "PEN";
  const pagada = ctx.pagadaPorId[id] || 0;
  const edificio = ctx.edificiosMap[id];

  if (!edificio) {
    return {
      edificioId: id,
      edificioNombre: `Edificio #${id}`,
      bruto: 0,
      neto: 0,
      comisionIntermediario: 0,
      generada: 0,
      recaudada: 0,
      pagada,
      saldo: 0 - pagada,
      moneda,
    };
  }

  const fallback = getDolarFallback(pais);

  const enriquecerPauta = (factura) => {
    const importeNetoTotal = calcularImporteNetoIntermediario(factura, ctx.clientes, ctx.intermediarios, pais);
    const pantallasAjustadas = calcularPantallasAjustadas(edificio, factura);
    const participacion = factura.pantallastotales > 0 ? pantallasAjustadas / factura.pantallastotales : 0;
    const importeNetoProporcional = participacion * importeNetoTotal;
    const rate = getRateForOpp(factura, getDolarValue, fallback);
    return {
      ...factura,
      importeNeto: factura.moneda_dolar ? importeNetoProporcional * rate : importeNetoProporcional,
      importe: factura.moneda_dolar ? factura.importe * rate : factura.importe,
      isBTL: false,
    };
  };

  const lista = ctx.pautaTodos.filter((f) => perteneceA(f.edificiosparticipantes, id)).map(enriquecerPauta);
  const contratosEdificio = ctx.facturasContratos
    .filter((f) => perteneceA(f.edificiosparticipantes, id))
    .map(enriquecerPauta);

  const btlFiltradas = ctx.btlData
    .filter((s) => s.pais === pais && !s.eliminada)
    .map((s) => ({
      ...s,
      buildings: typeof s.buildings === "string" ? JSON.parse(s.buildings) : s.buildings || [],
    }))
    .filter((s) => (s.buildings || []).some((b) => b.id === id))
    .map((s) => {
      const building = (s.buildings || []).find((b) => b.id === id) || s.buildings[0];
      const cliente = ctx.clientes.find((c) => c.id === s.client_id && c.pais === pais);
      return {
        id: s.id,
        facturanumero: s.facturanumero || "-",
        facturafecha: s.facturafecha,
        facturafechacobro: s.facturafechacobro,
        cliente: cliente ? cliente.nombre : "SIN CLIENTE",
        importe: building ? building.importe : 0,
        comision: building ? building.comision : 0,
        pantallastotales: 0,
        isBTL: true,
        moneda_dolar: s.moneda_dolar,
        buildings: s.buildings,
        client_id: s.client_id,
      };
    });

  const edificioComisionObj = {
    comision_1_porcentaje: edificio.comision_1_porcentaje,
    comision_1_fechainicio: edificio.comision_1_fechainicio,
    comision_1_fechafin: edificio.comision_1_fechafin,
    comision_2_porcentaje: edificio.comision_2_porcentaje,
    comision_2_fechainicio: edificio.comision_2_fechainicio,
    comision_2_fechafin: edificio.comision_2_fechafin,
    comision_3_porcentaje: edificio.comision_3_porcentaje,
    comision_3_fechainicio: edificio.comision_3_fechainicio,
    comision_3_fechafin: edificio.comision_3_fechafin,
  };

  const todas = [...lista, ...contratosEdificio, ...btlFiltradas];

  let totalBruto = 0;
  let totalNeto = 0;
  let totalIntermediario = 0;
  let totalGenerada = 0;
  let totalRecaudada = 0;

  todas.forEach((f) => {
    const pantallasAj = f.isBTL ? null : calcularPantallasAjustadas(edificio, f);
    const participacion = f.isBTL ? 1 : f.pantallastotales > 0 ? pantallasAj / f.pantallastotales : 0;
    const bruto = f.isBTL ? f.importe : participacion * f.importe;
    const neto = f.importeNeto || bruto;
    const porc = calcularPorcentajeComisionEdificio(edificioComisionObj, f.facturafecha);
    const comision = neto * (porc / 100);
    totalBruto += bruto;
    totalNeto += neto;
    totalIntermediario += bruto - neto;
    totalGenerada += comision;
    if (f.facturafechacobro) totalRecaudada += comision;
  });

  return {
    edificioId: id,
    edificioNombre: edificio.edificio || `Edificio #${id}`,
    bruto: totalBruto,
    neto: totalNeto,
    comisionIntermediario: totalIntermediario,
    generada: totalGenerada,
    recaudada: totalRecaudada,
    pagada,
    saldo: totalRecaudada - pagada,
    moneda,
  };
};

/**
 * Saldo de UN edificio. → {generada, recaudada, pagada, saldo, bruto, neto, comisionIntermediario}
 */
export const getSaldoEdificio = async ({ pais, edificioId }) => {
  const ctx = await cargarContexto(pais, [Number(edificioId)]);
  const r = computeEdificio(edificioId, ctx, pais);
  return {
    generada: r.generada,
    recaudada: r.recaudada,
    pagada: r.pagada,
    saldo: r.saldo,
    bruto: r.bruto,
    neto: r.neto,
    comisionIntermediario: r.comisionIntermediario,
  };
};

/**
 * Saldo de TODOS los edificios operativos del país en una sola pasada (data compartida fetcheada una
 * vez). → Array<{edificioId, edificioNombre, generada, recaudada, pagada, saldo, moneda}>
 */
export const getSaldosEdificios = async ({ pais }) => {
  await loadExchangeRates();
  const operativos = await getEdificiosOperativos({ pais }).catch(() => []);
  const ids = (operativos || []).map((e) => Number(e.id));
  if (ids.length === 0) return [];
  const ctx = await cargarContexto(pais, ids, operativos);
  return operativos.map((e) => computeEdificio(e.id, ctx, pais));
};
